import { useCallback, useEffect, useState } from 'react'
import type {
  Account,
  Category,
  CreditCard,
  Invoice,
  OffsetLink,
  Transaction,
} from '@domain/finance'
import type { EnrichmentPatch } from '@domain/ports/finance-repository'
import { referenceMonthOf } from '@shared/date/reference-month'
import { useFinanceRepositories } from '@ui/finance/finance-context'

/**
 * Uma transação e o contexto que a tela de detalhe precisa para explicá-la.
 *
 * ## `notFound` é um estado, não um erro
 *
 * Um link colado para transação que não existe mais é o caso normal — a URL é
 * compartilhável de propósito. A tela sabe dizer isso melhor que um `throw`, e
 * misturar "não existe" com "a rede caiu" faria a mesma tela pedir para tentar
 * de novo uma coisa que nunca vai aparecer.
 *
 * ## As faturas vêm do mês da COMPRA, não do mês corrente
 *
 * Numa compra de cartão a tela aponta para a fatura em que ela caiu, e essa
 * fatura pertence ao ciclo da compra. Buscar as do mês corrente acertaria por
 * acidente na compra deste mês e erraria em toda a base histórica.
 *
 * ## E a leitura da fatura não bloqueia
 *
 * Ela serve a uma frase explicativa. A transação, as categorias e os cartões vão
 * juntos porque sem os três não há tela; a fatura chega quando chegar.
 */
export type TransactionDetail =
  | { status: 'loading' | 'failed' | 'notFound' }
  | {
      status: 'loaded'
      transaction: Transaction
      categories: Category[]
      creditCards: CreditCard[]
      accounts: Account[]
      /** A fatura a que a compra pertence, quando há uma. */
      invoice: Invoice | null
      /** Aplica no que já está carregado a edição que acabou de ser gravada. */
      patchEnrichment: (patch: EnrichmentPatch) => void
      /**
       * Relê SÓ as categorias. Existe porque o seletor de tela cheia cria uma
       * daqui: sem isto ela não apareceria na lista até recarregar a página.
       * Reler a transação junto seria pagar quatro leituras por causa de uma.
       */
      recarregarCategorias: () => void
      /** O vínculo de abatimento da entrada, depois que a folha gravou. */
      aplicarVinculo: (offsetOf: OffsetLink | null) => void
    }

type Settled =
  | { id: string; status: 'failed' | 'notFound' }
  | {
      id: string
      status: 'loaded'
      transaction: Transaction
      categories: Category[]
      creditCards: CreditCard[]
      accounts: Account[]
      invoice: Invoice | null
    }

export function useTransaction(id: string): TransactionDetail {
  const {
    transactionRepository,
    categoryRepository,
    creditCardRepository,
    accountRepository,
    invoiceRepository,
  } = useFinanceRepositories()
  const [settled, setSettled] = useState<Settled | null>(null)

  useEffect(() => {
    let live = true

    void Promise.all([
      transactionRepository.byId(id),
      categoryRepository.list(),
      creditCardRepository.list(),
      accountRepository.list(),
    ])
      .then(async ([transaction, categories, creditCards, accounts]) => {
        if (!live) return
        if (transaction === null) {
          setSettled({ id, status: 'notFound' })
          return
        }
        // Só compra de cartão pertence a uma fatura, e o `invoiceId` já diz
        // qual — perguntar por mês e depois procurar seria refazer a decisão
        // que a ingestão tomou (decisão 137a).
        const invoice =
          transaction.invoiceId === null
            ? null
            : ((
                await invoiceRepository.listByMonth(
                  referenceMonthOf(transaction.source.transactionDate),
                )
              ).find((f) => f.id === transaction.invoiceId) ?? null)

        if (live) {
          setSettled({
            id,
            status: 'loaded',
            transaction,
            categories,
            creditCards,
            accounts,
            invoice,
          })
        }
      })
      .catch((error: unknown) => {
        console.error('useTransaction: a leitura da transação falhou', error)
        if (live) setSettled({ id, status: 'failed' })
      })

    return () => {
      live = false
    }
  }, [
    transactionRepository,
    categoryRepository,
    creditCardRepository,
    accountRepository,
    invoiceRepository,
    id,
  ])

  const patchEnrichment = useCallback((patch: EnrichmentPatch) => {
    setSettled((atual) => {
      if (atual === null || atual.status !== 'loaded') return atual
      const { transaction } = atual
      return {
        ...atual,
        transaction: {
          ...transaction,
          enrichment: {
            ...transaction.enrichment,
            // Campo ausente é campo não tocado — a mesma semântica do adapter,
            // da dublê e de `useMonthlyBook`. As quatro dizem o mesmo de
            // propósito: um patch que se comportasse diferente aqui faria a
            // tela discordar do banco sem ninguém perceber.
            categoryId:
              patch.categoryId === undefined ? transaction.enrichment.categoryId : patch.categoryId,
            note: patch.note === undefined ? transaction.enrichment.note : patch.note,
            merchantId:
              patch.merchantId === undefined ? transaction.enrichment.merchantId : patch.merchantId,
          },
        },
      }
    })
  }, [])

  /** O vínculo da entrada, depois que a folha gravou — sem reler o documento. */
  const aplicarVinculo = useCallback((offsetOf: OffsetLink | null) => {
    setSettled((atual) =>
      atual === null || atual.status !== 'loaded'
        ? atual
        : {
            ...atual,
            transaction: {
              ...atual.transaction,
              enrichment: { ...atual.transaction.enrichment, offsetOf },
            },
          },
    )
  }, [])

  const recarregarCategorias = useCallback(() => {
    void categoryRepository
      .list()
      .then((categories) => {
        setSettled((atual) =>
          atual === null || atual.status !== 'loaded' ? atual : { ...atual, categories },
        )
      })
      .catch((error: unknown) => {
        console.error('useTransaction: a releitura das categorias falhou', error)
      })
  }, [categoryRepository])

  // O id carimbado no resultado, pelo mesmo motivo do mês em `useMonthlyBook`:
  // resultado de outra transação **é** carregando.
  if (settled === null || settled.id !== id) return { status: 'loading' }
  if (settled.status !== 'loaded') return { status: settled.status }
  return {
    status: 'loaded',
    transaction: settled.transaction,
    categories: settled.categories,
    creditCards: settled.creditCards,
    accounts: settled.accounts,
    invoice: settled.invoice,
    patchEnrichment,
    recarregarCategorias,
    aplicarVinculo,
  }
}
